/**
 * IMPORTS
 */

import { useMemo } from "react";
import { useLocation } from "react-router-dom";

import { BarChart3 } from "lucide-react";

import { useCobrancaCreditosCliente } from "@/hooks/cobranca-credito-cliente/buscar-cobranca-por-cliente";
import { useBuscarPorUsuario } from "@/hooks/usuários/buscar-por-um-cliente";

import { DynamicTable } from "@/presentation/components/table-custom/table";

import type { ILinhaCobrancaCreditoCliente } from "@/interface/extrato-credito-cliente";
import { getStatusLabel } from "@/utils/get-status-label";

import { BarraFiltrosCobrancaCredito } from "./components/barra-filtros-cobranca-credito";
import { ResumoConsumoCredito } from "./components/resumo-consumo-credito";
import type { ILinhaCobrancaTabelaCliente } from "./interface";

function formatarDataHoraCobranca(isoData: string): string {
  try {
    return new Date(isoData).toLocaleString("pt-BR", {
      dateStyle: "short",
      timeStyle: "short",
    });
  } catch {
    return isoData;
  }
}

function formatarQuantidadeCredito(quantidade: number): string {
  return Number.isInteger(quantidade) ? String(quantidade) : quantidade.toFixed(2);
}

function formatarMoeda(valor: number): string {
  return valor.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function formatarPeriodoResumo(dataInicio?: string, dataFim?: string): string {
  if (!dataInicio && !dataFim) {
    return "Todos os períodos";
  }
  try {
    const fmt = (iso: string) =>
      new Date(`${iso}T12:00:00`).toLocaleDateString("pt-BR", { dateStyle: "short" });
    if (dataInicio && dataFim) {
      return `${fmt(dataInicio)} — ${fmt(dataFim)}`;
    }
    if (dataInicio) {
      return `A partir de ${fmt(dataInicio)}`;
    }
    return `Até ${fmt(dataFim!)}`;
  } catch {
    return "Período selecionado";
  }
}

function gerarPaginasNavegacao(currentPage: number, lastPage: number): Array<number | string> {
  if (lastPage <= 1) {
    return [1];
  }

  const paginas: Array<number | string> = [];
  paginas.push(1);

  const inicioJanela = Math.max(2, currentPage - 1);
  const fimJanela = Math.min(lastPage - 1, currentPage + 1);

  if (inicioJanela > 2) {
    paginas.push("...");
  }

  for (let pagina = inicioJanela; pagina <= fimJanela; pagina += 1) {
    paginas.push(pagina);
  }

  if (fimJanela < lastPage - 1) {
    paginas.push("...");
  }

  paginas.push(lastPage);

  return paginas;
}

function mapearLinhaCobrancaParaTabela(
  linha: ILinhaCobrancaCreditoCliente
): ILinhaCobrancaTabelaCliente {
  const situacao = linha.remessa?.situacao ?? linha.remessa?.status ?? "-";
  const situacaoTexto = String(situacao);
  const rotuloSituacao = getStatusLabel(situacaoTexto);

  return {
    id: linha.id,
    dataHoraMovimentacao: formatarDataHoraCobranca(linha.created_at),
    nomeProduto: linha.produto?.nome ?? "-",
    numeroRemessa: linha.remessa?.numero_remessa ? `#${linha.remessa.numero_remessa}` : "-",
    quantidadeCreditosFormatada: formatarQuantidadeCredito(linha.quantidade_creditos),
    valorUnitarioFormatado: formatarMoeda(linha.valor_unitario_credito),
    valorTotalLinhaFormatado: formatarMoeda(linha.valor_total_linha),
    situacaoRemessa:
      rotuloSituacao !== "Status desconhecido" ? rotuloSituacao : situacaoTexto.replace(/_/g, " "),
  };
}

function CobrancaCreditosCliente() {
  const location = useLocation();
  const { cliente } = location.state || {};
  const clienteIdParaCobranca = cliente?.id as number | undefined;

  const { user: clienteDados, isLoading: carregandoNomeCliente } = useBuscarPorUsuario(
    clienteIdParaCobranca ?? 0
  );
  const nomeCliente = clienteDados?.nome;

  const {
    linhasCobranca,
    paginacaoCobranca,
    totaisCobranca,
    totaisConsumoPorProduto,
    carregandoCobranca,
    buscandoCobranca,
    erroCobranca,
    paginaAtualCobranca,
    itensPorPaginaCobranca,
    definirPaginaAtualCobranca,
    filtrosCobranca,
    definirFiltrosCobranca,
    limparFiltrosCobranca,
    aplicarFiltroMesAtual,
    opcoesProdutoFiltroCobranca,
    carregandoOpcoesProdutoCobranca,
  } = useCobrancaCreditosCliente({ clienteId: clienteIdParaCobranca });

  const resumoPeriodo = useMemo(
    () => formatarPeriodoResumo(filtrosCobranca.dataInicio, filtrosCobranca.dataFim),
    [filtrosCobranca.dataInicio, filtrosCobranca.dataFim]
  );

  const cobrancaCarregandoResumo = carregandoCobranca || buscandoCobranca;
  const cobrancaCarregandoTabela = carregandoCobranca;

  const linhasTabelaCobranca = useMemo(
    () => linhasCobranca.map(mapearLinhaCobrancaParaTabela),
    [linhasCobranca]
  );

  const paginaAtual = paginaAtualCobranca;
  const ultimaPagina = paginacaoCobranca?.last_page ?? 1;
  const totalRegistros = paginacaoCobranca?.total ?? 0;

  const paginasNavegacao = useMemo(
    () => gerarPaginasNavegacao(paginaAtual, ultimaPagina),
    [paginaAtual, ultimaPagina]
  );

  const colunasCobranca = useMemo(
    () => [
      { label: "Data", accessor: "dataHoraMovimentacao" },
      { label: "Produto", accessor: "nomeProduto", limit: 40 },
      { label: "Remessa", accessor: "numeroRemessa" },
      { label: "Qtd. créditos", accessor: "quantidadeCreditosFormatada" },
      { label: "Valor unit.", accessor: "valorUnitarioFormatado" },
      { label: "Valor total", accessor: "valorTotalLinhaFormatado" },
      { label: "Situação", accessor: "situacaoRemessa", isBadge: true },
    ],
    []
  );

  const onGoToPage = (page: number | "prev" | "next") => {
    if (page === "prev") {
      definirPaginaAtualCobranca(Math.max(1, paginaAtualCobranca - 1));
      return;
    }

    if (page === "next") {
      definirPaginaAtualCobranca(Math.min(ultimaPagina, paginaAtualCobranca + 1));
      return;
    }

    definirPaginaAtualCobranca(page);
  };

  if (!clienteIdParaCobranca) {
    return (
      <div className="rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
        Não foi possível identificar o cliente. Acesse esta tela a partir da lista de clientes para
        visualizar a cobrança de créditos.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 border-b border-neutral-200 pb-3">
        <BarChart3 className="h-5 w-5 text-neutral-700" />
        <h2 className="text-lg font-semibold text-neutral-900">Cobrança de créditos</h2>
      </div>

      <p className="text-sm font-medium text-neutral-800">
        Cliente:{" "}
        {carregandoNomeCliente ? (
          <span className="text-muted-foreground">Carregando...</span>
        ) : (
          <span className="font-semibold">{nomeCliente || "N/A"}</span>
        )}
      </p>

      <p className="text-sm text-muted-foreground">
        Consumo faturável de remessas no período selecionado, com totais por produto.
      </p>

      {erroCobranca && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          Não foi possível carregar a cobrança. Tente novamente em instantes.
        </div>
      )}

      <ResumoConsumoCredito
        totais={totaisCobranca}
        totaisPorProduto={totaisConsumoPorProduto}
        carregando={cobrancaCarregandoResumo}
        resumoPeriodo={resumoPeriodo}
      />

      <DynamicTable
        title="Remessas faturáveis"
        description="Consumos de crédito elegíveis para cobrança no período"
        isLoadingPage={cobrancaCarregandoTabela}
        data={linhasTabelaCobranca}
        totalItems={totalRegistros}
        itemsPerPage={itensPorPaginaCobranca}
        columns={colunasCobranca}
        paginations={paginasNavegacao}
        onGoToPage={onGoToPage}
        currentPage={paginaAtual}
        lastPage={ultimaPagina}
        childerSearch={
          <BarraFiltrosCobrancaCredito
            filtros={filtrosCobranca}
            onAlterarFiltros={definirFiltrosCobranca}
            onLimparFiltros={limparFiltrosCobranca}
            onAplicarMesAtual={aplicarFiltroMesAtual}
            opcoesProduto={opcoesProdutoFiltroCobranca}
            carregandoProdutos={carregandoOpcoesProdutoCobranca}
          />
        }
      />
    </div>
  );
}

export { CobrancaCreditosCliente };
